import { useState } from "react";
import { usePrintPreview } from "@/hooks/usePrintPreview";
import PrintDialog from "@/components/shared/PrintDialog";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatNumber, formatMoney } from "@/lib/utils";
import { formatUnitQty } from "@apv-erp/status";
import { groupPurchaseOrderLines } from "@/lib/purchase-order-lines";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Printer,
  AlertCircle,
  Trash2,
  Loader2,
  Pencil,
  Package,
} from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { getStatusConfig } from "@/config/status-registry";
import { EntityLink } from "@/components/shared/EntityLink";
import ImageLightbox from "@/components/shared/ImageLightbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import WorkflowStepper from "@/components/shared/WorkflowStepper";
import { useWorkflowSteps } from "@/hooks/useWorkflowSteps";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";

const fmtD = (d: string | Date | null | undefined) => formatDate(d) || "—";

export default function PurchasingDetailPage() {
  const { poId } = useParams({ strict: false }) as { poId: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const { workflow } = useWorkflowSteps("purchasing");

  const handleUpdateStatus = async (newStatus: string) => {
    try {
      await trpcClient.purchaseOrders.updateStatus.mutate({
        id: poId,
        status: newStatus as any,
      });
      await queryClient.invalidateQueries({
        queryKey: ["purchaseOrder", poId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["activity-log", "purchase-order", poId],
      });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi cập nhật trạng thái");
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await trpcClient.purchaseOrders.delete.mutate({ id: poId });

      navigate({ to: "/purchasing" });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi xóa");
    } finally {
      setIsDeleting(false);
    }
  };

  // PurchaseOrder findOne includes: vendor: true
  const {
    data: poData,
    isLoading,
    error,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ["purchaseOrder", poId],
    queryFn: async () => trpcClient.purchaseOrders.getById.query({ id: poId }),
  });

  const po = poData as any;

  // Gõ/dán URL có id sai (404) hoặc không tải được: không hiển thị khung tải vô hạn
  if (!isLoading && !po) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Mua hàng", to: "/purchasing" }]}
        entityLabel="Đơn mua hàng"
        backTo="/purchasing"
        codeOrId={poId}
        error={error}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !po) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Mua hàng", to: "/purchasing" }]}
        title="Đang tải..."
      >
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </StandardDetailLayout>
    );
  }

  if (error) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Mua hàng", to: "/purchasing" }]}
        title="Không tìm thấy"
      >
        <div className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <Button onClick={() => navigate({ to: "/purchasing" })}>
            Quay lại
          </Button>
        </div>
      </StandardDetailLayout>
    );
  }

  const statusCfg = getStatusConfig("PurchaseOrderStatus", po.status);
  const items: any[] = po.items || [];
  // Hiển thị 1 dòng / vật tư (05/11/2026): đơn lập từ nhiều LSX lưu 1 dòng / kế hoạch
  const displayItems: any[] = groupPurchaseOrderLines(items);

  return (
    <StandardDetailLayout
      breadcrumbs={[{ label: "Mua hàng", to: "/purchasing" }]}
      title={po.poId}
      entityId={po.id}
      entityType="purchase_order"
      timestamps={{ created: po.createdAt, updated: po.updatedAt }}
      status={
        <Badge variant="outline" className={statusCfg.color}>
          {statusCfg.label || po.status}
        </Badge>
      }
      actions={
        <div className="flex gap-1.5 flex-wrap">
          {po.status === "DRAFT" && (
            <Button size="sm" onClick={() => handleUpdateStatus("PENDING")}>
              Trình duyệt
            </Button>
          )}
          {po.status === "PENDING" && (
            <Button size="sm" onClick={() => handleUpdateStatus("APPROVED")}>
              Duyệt PO
            </Button>
          )}
          {po.status === "APPROVED" && (
            <Button size="sm" onClick={() => handleUpdateStatus("ORDERED")}>
              Đã đặt hàng
            </Button>
          )}
          {/* Nhận hàng: KCS vật tư → phiếu nhập kho theo đơn tự cập nhật Nhận 1 phần / Đã nhận đủ (bỏ nút chuyển tay, 05/11/2026) */}
          {po.status !== "CANCELLED" && po.status !== "RECEIVED" && po.status !== "PARTIAL_RECEIVED" && (
            <Button
              size="sm"
              variant="outline"
              className="text-amber-600 border-amber-200"
              onClick={() => handleUpdateStatus("CANCELLED")}
            >
              Hủy đơn
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPrint(true)}
          >
            <Printer className="mr-1.5 h-3.5 w-3.5" /> In
          </Button>
          {/* Sửa / Xoá chỉ khi Bản nháp / Chờ duyệt (xoá thêm đơn Đã huỷ chưa nhận) — khớp máy chủ */}
          {["DRAFT", "PENDING"].includes(po.status) && (
            <Button
              variant="default"
              size="sm"
              data-testid="po-edit-btn"
              onClick={() =>
                (navigate as any)({
                  to: "/purchasing/$poId/edit",
                  params: { poId },
                })
              }
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" /> Sửa
            </Button>
          )}
          {["DRAFT", "PENDING", "CANCELLED"].includes(po.status) &&
            !(po.items || []).some((it: any) => Number(it.receivedQty || 0) > 0) && (
              <Button
                variant="outline"
                size="sm"
                data-testid="po-delete-btn"
                className="text-rose-600 border-rose-200 hover:bg-rose-50"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Xoá
              </Button>
            )}
        </div>
      }
      onBack={() => navigate({ to: "/purchasing" })}
      workflow={
        <WorkflowStepper
          workflow={workflow}
          currentStatus={po.status}
          onStatusChange={handleUpdateStatus}
          // Nhận 1 phần / Đã nhận đủ do phiếu nhập kho cập nhật (05/11/2026), không bấm chuyển tay
          lockedSteps={["PARTIAL_RECEIVED", "RECEIVED"]}
        />
      }
    >
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={`Xoá đơn mua hàng ${po.poId}?`}
        description="Hành động này không thể hoàn tác."
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />
      {/* ═══ 1. 4-COL INFO GRID — real PurchaseOrder schema fields ═══ */}
      <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/50 px-4 py-3">
          <h3 className="font-semibold text-slate-800 text-sm">
            Thông tin chung
          </h3>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-y-4 gap-x-8">
          <div className="col-span-1">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Mã PO
            </div>
            <div className="font-medium text-slate-900 font-mono text-indigo-600">
              {po.poId}
            </div>
          </div>
          <div className="col-span-1 md:col-span-2">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
              NCC
            </div>
            <div className="font-medium text-slate-900">
              {po.vendor ? (
                <EntityLink entityType="vendor" id={po.vendor.id} label={po.vendor.name} />
              ) : "—"}
            </div>
          </div>
          <div className="col-span-1">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Mã NCC
            </div>
            <div className="font-medium text-slate-900 font-mono">
              {po.vendor ? (
                <EntityLink entityType="vendor" id={po.vendor.id} code={po.vendor.code || po.vendor.id} />
              ) : "—"}
            </div>
          </div>

          <div className="col-span-1">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Ngày đặt
            </div>
            <div className="font-medium text-slate-900">
              {fmtD(po.orderDate)}
            </div>
          </div>
          <div className="col-span-1">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
              ETA Giao
            </div>
            <div className="font-medium text-slate-900">
              {fmtD(po.expectedDate)}
            </div>
          </div>
          <div className="col-span-1">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Ngày nhận
            </div>
            <div className="font-medium text-slate-900">
              {fmtD(po.receivedDate)}
            </div>
          </div>
          <div className="col-span-1">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Số lượng vật tư
            </div>
            <div className="font-medium text-slate-900">{items.length} mục</div>
          </div>

          <div className="col-span-1">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Tổng tiền
            </div>
            <div className="font-bold text-indigo-700 text-lg">
              {formatMoney(po.totalAmount)}
            </div>
          </div>
          <div className="col-span-1 md:col-span-3">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Trạng thái
            </div>
            <div>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusCfg.color}`}
              >
                {statusCfg.label || po.status}
              </span>
            </div>
          </div>

          <div className="col-span-1 md:col-span-4">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Ghi chú
            </div>
            <div className="text-slate-700 whitespace-pre-wrap">
              {po.notes || "—"}
            </div>
          </div>
        </div>
      </div>

      {/* ═══ 2. ITEMS TABLE ═══ */}
      <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
        <div className="px-3 py-2 border-b bg-slate-50/60 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Package className="h-3.5 w-3.5 text-indigo-500" />
            <span className="text-xs font-semibold text-slate-600">
              Chi tiết hàng hoá
            </span>
          </div>
          <span className="text-xs text-slate-400">{displayItems.length} dòng</span>
        </div>
        {items.length === 0 ? (
          <div className="text-center text-sm text-slate-400 py-6">
            Chưa có hàng hoá
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 text-center">STT</TableHead>
                <TableHead className="w-14 text-center">Hình ảnh</TableHead>
                <TableHead>Mã vật tư</TableHead>
                <TableHead>Tên vật tư</TableHead>
                <TableHead>LSX liên kết</TableHead>
                <TableHead className="text-right">SL đặt</TableHead>
                <TableHead className="text-right">SL nhận</TableHead>
                <TableHead className="text-right">Đơn giá</TableHead>
                <TableHead className="text-right">Thành tiền</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayItems.map((item: any, idx: number) => {
                const lineTotal =
                  Number(item.quantity || 0) * Number(item.unitPrice || 0);
                const prodOrder = item.productionOrder || item.materialPlan?.productionOrder;
                const matImage = item.material?.image || item.image;
                return (
                  <TableRow key={item.id || idx}>
                    <TableCell className="text-center text-slate-400">
                      {idx + 1}
                    </TableCell>
                    <TableCell className="text-center p-1.5">
                      {matImage ? (
                        <div className="inline-block rounded border border-slate-200 overflow-hidden shadow-xs hover:shadow-md transition-shadow">
                          <ImageLightbox
                            src={matImage}
                            alt={item.material?.name || "Vật tư"}
                            className="w-10 h-10 object-cover cursor-pointer hover:scale-105 transition-transform"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center mx-auto text-slate-300">
                          <Package className="w-4 h-4" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {item.material?.id || item.materialId ? (
                        <EntityLink
                          entityType="material"
                          id={item.material?.id || item.materialId}
                          code={item.material?.materialId || item.materialId}
                        />
                      ) : (
                        item.materialId || "—"
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">
                      {item.material?.name || "—"}
                      {item.notes && (
                        <div className="text-[11px] text-amber-800 mt-0.5 whitespace-pre-wrap" data-testid="po-line-note">Ghi chú: {item.notes}</div>
                      )}
                    </TableCell>
                    <TableCell data-testid="po-line-orders">
                      {item.__orders?.length ? (
                        <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                          {item.__orders.map((o: any) => (
                            <EntityLink key={o.id} entityType="productionOrder" id={o.id} code={o.orderId} />
                          ))}
                        </div>
                      ) : prodOrder ? (
                        <EntityLink
                          entityType="productionOrder"
                          id={prodOrder.id}
                          code={prodOrder.orderId}
                        />
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap" data-testid="po-line-qty">
                      {/* Mua theo ĐVT phụ: số đã đặt + quy đổi ra ĐVT chính (04/11/2026) */}
                      {item.entryUnit && item.entryQty != null ? (
                        <>
                          {formatUnitQty(Number(item.entryQty))} {item.entryUnit}
                          <div className="text-[11px] text-slate-500">= {formatUnitQty(Number(item.quantity))} {item.material?.unit}</div>
                        </>
                      ) : (
                        <>{formatUnitQty(Number(item.quantity))} {item.material?.unit}</>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-emerald-600 whitespace-nowrap">
                      {formatUnitQty(Number(item.receivedQty || 0))} {item.material?.unit}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {item.entryUnit && item.entryUnitPrice != null ? (
                        <>
                          {formatMoney(item.entryUnitPrice)} / {item.entryUnit}
                          <div className="text-[11px] text-slate-500">{formatMoney(item.unitPrice)} / {item.material?.unit}</div>
                        </>
                      ) : (
                        formatMoney(item.unitPrice)
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-bold">
                      {formatMoney(lineTotal)}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow className="bg-slate-50 border-t font-semibold">
                <TableCell
                  colSpan={8}
                  className="text-right text-xs text-slate-500 uppercase"
                >
                  Tổng cộng
                </TableCell>
                <TableCell className="text-right tabular-nums text-indigo-600 font-bold">
                  {formatMoney(po.totalAmount)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </div>

      <PrintDialog
        open={showPrint}
        onClose={() => setShowPrint(false)}
        type="purchase_order"
        data={po}
      />
    </StandardDetailLayout>
  );
}
